import {
   CalendarDays,
   Clock3,
   Eye,
   MapPin,
   PlayCircle,
   Volume2,
   X,
   Book,
   Check,
   BriefcaseBusiness,
   Podcast,
   ArrowLeft,
} from "lucide-react";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import React, { useEffect, useState } from "react";
import IvoryList from "../../../src/assets/icons/IvoryList.svg";
import IvoryGold from "../../../src/assets/icons/IvoryGold.svg";
import ImageSliderPopup from "./ImageSliderPopup";
import Person from "../../assets/images/person.png";
import ImgSlide from "../../assets/images/img-slider.png";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import getToken from "../../hooks/getToken";
import YouTube from "react-youtube";
import Image from "../ui/Image";
import DefaultImg from "../../assets/used-images/defaultImg.jpeg";
import { Dialog, DialogContent } from "@mui/material";
import { Box } from "@mui/system";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const List = ({ pdfs }) => {
   const { t } = useTranslation();
   const [selectedPdf, setSelectedPdf] = useState(null);
   const [open, setOpen] = useState(false);
   const token = getToken();
   const handleOpen = (pdf) => {
      setSelectedPdf(pdf);
      setOpen(true);
   };

   const handleClose = () => {
      setOpen(false);
      setSelectedPdf(null);
   };

   return (
      <>
         {
            token ?
               <>
                  <ul className="scrolling flex gap-2 overflow-auto px-2">
                     {pdfs.map((pdf, i) => (
                        <li key={i} className="relative">
                           <div className="w-full z-10 absolute bg-light-red h-full rounded-md"></div>
                           <div className="w-36"></div>
                           <Image
                              src={DefaultImg}
                              alt="Ivory PDF"
                              className="rounded-md w-full h-28"
                           />
                           <div className="flex gap-2 absolute bottom-2 right-2 z-20">
                              <button
                                 onClick={() => handleOpen(pdf?.pdf)}
                                 className="bg-primary-tw size-8 rounded-md flex justify-center items-center"
                              >
                                 <Eye size={20} color="white" />
                              </button>
                           </div>
                        </li>
                     ))}
                  </ul>

                  {/* MUI Dialog (Modal) */}
                  <Dialog
                     open={open}
                     onClose={handleClose}
                     fullWidth
                     maxWidth="lg"
                     PaperProps={{ sx: { height: "80vh", overflow: "hidden" } }} // Correct way to set modal height
                  >


                     <DialogContent dividers>
                        <Box sx={{ height: "75vh", overflow: "hidden" }}>
                           {selectedPdf && (
                              <iframe
                                 src={`${selectedPdf}#toolbar=0&navpanes=0&scrollbar=0`}
                                 style={{ width: "100%", height: "100%", border: "none" }}
                                 title="PDF Viewer"
                              />
                           )}
                        </Box>
                     </DialogContent>

                  </Dialog>

               </>
               :
               <div className="text-secondary-tw space-y-2">
                  <p className="text-xl">{t('custom.login-first')}</p>
                  <Link to={'/login'} className="text-primary-tw text-lg flex justify-center items-center gap-1">
                     {t('routes.login')} <ArrowLeft size={15} className="mt-1" />
                  </Link>
               </div>
         }
         {/* PDF List */}
      </>
   );
};

const ImgSlider = ({ title, imgs }) => {
   const [imgData, setImgData] = useState({
      see: false,
      imgIndex: null,
      cTitle: title,
   });

   return (
      <div>
         <Swiper
            spaceBetween={12}
            slidesPerView="auto"
            grabCursor={true}
            freeMode={true}
            className="w-full"
         >
            {imgs.map((img, i) => (
               <SwiperSlide
                  key={i}
                  className="!w-48"
               >
                  <div
                     className="w-full rounded-md cursor-pointer"
                     onClick={() =>
                        setImgData((prev) => ({
                           ...prev,
                           see: !prev.see,
                           imgIndex: i,
                        }))
                     }
                  >
                     <div className="w-48 h-40 relative">
                        <Image
                           src={img?.image || ImgSlide}
                           alt="img-from-coach"
                           className="w-full h-full object-cover rounded-md"
                        />
                     </div>
                  </div>
               </SwiperSlide>
            ))}
         </Swiper>

         <ImageSliderPopup imgData={imgData} setImgData={setImgData} imgs={imgs} />
      </div>
   );
};

const CourseVideo = ({ title, video }) => {
   const [seeVid, setSeeVid] = useState(false);
   const [player, setPlayer] = useState(null); // Store YouTube player instance

   const getId = () => {
      const isMatched = video.match(/[?&]v=([^&]+)/);
      return isMatched ? isMatched[1] : null;
   };

   const videoId = getId();

   const isVideoReady = (e) => {
      setPlayer(e.target);
   };

   const stopVidAndHide = () => {
      setSeeVid((prev) => !prev);
      if (player) {
         player.stopVideo();
      }
   };

   const poster = videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : DefaultImg; // Use default if no YouTube ID found

   return (
      <>
         <div
            className="cursor-pointer"
            onClick={() => setSeeVid((prev) => !prev)}
         >
            <div className="w-full h-40 bg-gray-200 rounded-md relative flex justify-center items-center">
               <span className="absolute z-10">
                  <PlayCircle size={25} color="white" />
               </span>
               <div className="absolute w-full h-full top-0 bg-[#0000009E] rounded-lg"></div>
               {/* Poster */}
               <img
                  src={poster}
                  alt="Video Thumbnail"
                  className="object-cover h-40 w-full object-center rounded-lg"
               />
            </div>
         </div>

         {/* Video Player */}
         <div
            className={`fixed top-0 left-0 ${seeVid
               ? " bg-[#00000099] w-full h-dvh z-30 opacity-100"
               : "opacity-0 z-0 h-0 w-0"
               } transition-all duration-150 ease-in-out px-24 py-10 max-lg:px-4`}
         >
            <div className="bg-primary-tw w-full p-2 rounded-t-md py-1 flex justify-between items-center">
               <p className="text-white text-base">
                  {title}
               </p>
               <div
                  className="mx-2 closeIcon cursor-pointer"
                  title="Close"
                  onClick={stopVidAndHide}
               >
                  <X size={17} color="white" />
               </div>
            </div>
            <div className="w-full h-[35rem] max-lg:h-[20rem] rounded-b-md flex items-center justify-center z-[9999] relative">
               <div className="absolute w-full h-full top-0 bg-[#0000009E]"></div>
               <YouTube
                  videoId={videoId}
                  onReady={isVideoReady}
                  className="w-full h-full object-cover"
               />
            </div>
         </div>
      </>
   );
};

const Confirmation = ({ state }) => {
   return (
      <div
         className="flex flex-col justify-items-center w-full items-center !mt-4"
      >
         <div className="rounded-full bg-secondary-tw p-2 text-white font-black">
            {state.state ? <Check size={30} fontWeight={900} /> : <X size={30} fontWeight={900} />}
         </div>
         <p className="text-lg !my-3">
            {state.message}
         </p>
      </div>
   );
};

const Topics = ({ topics }) => {
   return (
      <div>
         {
            topics && topics?.length !== 0 ?
               <>
                  {
                     topics?.map((topic, i) => (
                        <div className="flex items-start gap-2 text-start" key={i}>
                           <span className="w-3">
                              <img src={IvoryGold} alt="ivory-list" className="w-full mt-[2px]" />
                           </span>
                           <p className="text-primary-tw text-base font-semibold mb-2">
                              {topic?.title}
                           </p>
                        </div>
                     ))
                  }
               </>
               :
               <p className="text-start text-primary-tw text-lg font-semibold my-2">
                  عفوا لا يوجد محاور للدورة حاليا.
               </p>
         }
      </div>
   )
}

const SeminarDetailBox = ({ details, coach, seminar_id, title }) => {
   const { t } = useTranslation();
   const images = details?.attachments?.filter((item) => item.type === "image");
   const pdfs = details?.attachments?.filter((item) => item.type === "pdf");

   const getFirstSectionDefault = () => {
      if (pdfs?.length > 0) {
         return 1;
      }

      if (!pdfs?.length > 0) {
         if (!images?.length > 0) {
            if (!details?.video) {
               return 5;
            }
            return 3;
         }
         return 2;
      }

      return 5;
   };

   const [seenSection, setSeenSection] = useState(getFirstSectionDefault());
   const { id } = useParams();
   const token = getToken();
   const { URL } = useLanguage();
   const [dateState, setDateState] = useState({
      state: null,
      message: null
   });
   const nowFullDate = new Date().toLocaleDateString();
   const seminarDate = details?.date?.replaceAll('-', '/');

   const nowTimeParsed = Date.parse(nowFullDate);
   const seminarTimeParsed = Date.parse(seminarDate);

   useEffect(() => {
      if (nowTimeParsed > seminarTimeParsed) {
         setDateState({
            state: false,
            message: t('seminars.no-time')
         });
      } else {
         setDateState({
            state: true,
            message: t('custom.done')
         });
      }
   }, [details, nowTimeParsed, seminarTimeParsed]);

   const registerForSeminar = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
         Swal.fire({
            icon: "warning",
            title: t('seminars.warn'),
            text: t('custom.login-first'),
            confirmButtonText: t('custom.accept'),
         });
         return;
      }

      if (!id) {
         Swal.fire({
            icon: "error",
            title: t('custom.err-title'),
            text: t('custom.err'),
            confirmButtonText: t('custom.accept'),
         });
         return;
      }

      try {
         const response = await fetch(
            `${URL}/seminar-register?seminar_id=${id}`,
            {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
               },
               body: JSON.stringify({
                  seminar_id: id,
               }),
            }
         );

         const result = await response.json();

         if (response.ok) {
            Swal.fire({
               icon: "success",
               title: t('custom.done-check'),
               text: t('seminars.check-p'),
               confirmButtonText: t('custom.accept'),
            });
         } else if (response.status === 409) {
            Swal.fire({
               icon: "info",
               title: t('custom.old-check'),
               text: result.message,
               confirmButtonText: t('custom.accept'),
            });
         } else {
            Swal.fire({
               icon: "error",
               title: t('custom.err-title'),
               text: t('custom.err'),
               confirmButtonText: t('custom.accept'),
            });
         }
      } catch (error) {
         Swal.fire({
            icon: "error",
            title: t('custom.err-title'),
            text: t('custom.err'),
            confirmButtonText: t('custom.accept'),
         });
      }
   };

   const returnedSection = {
      1: <List pdfs={pdfs} />,
      2: <ImgSlider title={title} imgs={images} />,
      3: <CourseVideo title={title} video={details?.video} />,
      4: <Confirmation state={dateState} />,
      5: <Topics topics={details?.courses[0]?.trainingProgram || []} />
   };

   return (
      <div className="border p-3 rounded-md mb-3 grid grid-cols-[59%_39%] gap-4 max-lg:grid-cols-1">

         {/* Course details */}
         <div className="flex items-start gap-4 max-md:flex-col">
            {/* coach image */}
            <div className="w-48 h-64 max-lg:w-full max-lg:h-full">
               <div className="w-48 max-lg:w-full"></div>
               <img
                  src={coach?.profile_picture || Person}
                  alt={`${coach?.first_name} ${coach?.last_name}`}
                  className="w-full h-full object-cover rounded-md"
               />
            </div>

            <ul>
               {/* DATE */}
               {
                  details?.date &&
                  <li className="flex items-center gap-2 text-semi-gray">
                     <span>
                        <CalendarDays size={20} color="white" fill="var(--semi-gray)" />
                     </span>
                     <span className="text-base font-sans">{details?.date}</span>
                  </li>
               }

               {/* TIME */}
               {
                  details?.start_time &&
                  <li className="flex items-center gap-2 text-semi-gray my-3 flex-wrap">
                     <span>
                        <Clock3 size={20} color="white" fill="var(--semi-gray)" />
                     </span>
                     <span className="text-base font-sans">
                        {details?.start_time} - {" "}
                     </span>
                     <span className="text-base font-sans">{details?.end_time}</span>
                  </li>
               }

               {/* LANG */}
               {
                  details?.languages &&
                  <li className="flex items-center gap-2 text-semi-gray my-3 flex-wrap">
                     <span>
                        <Volume2 size={20} color="" fill="var(--semi-gray)" />
                     </span>
                     <span className="text-base">
                        {details?.languages?.title}
                     </span>
                  </li>
               }

               {/* LOCATION */}
               {
                  details?.hall &&
                  <li className="flex items-center gap-2 text-semi-gray my-3 flex-wrap">
                     <span>
                        <MapPin size={20} color="white" fill="var(--semi-gray)" />
                     </span>
                     <span className="text-base">
                        {details?.hall?.title + " - " || "مقر ايفوري"}
                     </span>
                     <span className="text-base">
                        {details?.hall?.country?.title || ""}
                     </span>
                  </li>
               }

               {/* COACH */}
               {
                  coach?.first_name &&
                  <li className="flex items-center gap-2 text-semi-gray my-3">
                     <span>
                        <Podcast size={20} />
                     </span>
                     <span className="text-base text-nowrap">
                        {coach?.first_name + " " + coach?.last_name || ""}
                     </span>
                  </li>
               }

               {/* specialty */}
               {
                  coach?.specialty &&
                  <li className="flex items-start gap-2 text-semi-gray my-3">
                     <span>
                        <BriefcaseBusiness size={20} />
                     </span>
                     <span className="text-sm font-medium text-start">
                        {coach?.specialty}
                     </span>
                  </li>
               }

               {/* course */}
               {
                  details?.courses &&
                  <li className="flex items-start gap-2 text-semi-gray my-3 flex-wrap">
                     <span>
                        <Book size={20} />
                     </span>
                     <div className="flex gap-2 items-center flex-wrap">
                        {
                           details?.courses?.map((course, i) => (
                              <span key={i} className="text-sm font-medium text-start">
                                 {course?.title}
                              </span>
                           ))
                        }
                     </div>
                  </li>
               }
            </ul>
         </div>

         {/* Course Content */}
         <div className="bg-light-gray p-3 rounded-md relative w-full pt-5 max-lg:w-full mt-4">
            <div className="flex gap-2 items-center mb-4 absolute flex-wrap -top-6 right-0 max-lg:gap-1">
               {
                  !token ?
                     <button
                        className={`border
                  ${seenSection === 1
                              ? "seenSection"
                              : "unSeenSection"
                           } flex items-center gap-2 px-3 py-1 rounded-tl-xl`}
                        onClick={() => setSeenSection(1)}
                     >
                        <span>
                           <img
                              src={seenSection === 1 ? IvoryList : IvoryGold}
                              alt="list-item"
                              className="w-3 mt-1"
                           />
                        </span>
                        <span>{t('seminars.content')}</span>
                     </button>
                     : (token && pdfs?.length > 0) ?
                        <button
                           className={`border
                  ${seenSection === 1
                                 ? "seenSection"
                                 : "unSeenSection"
                              } flex items-center gap-2 px-3 py-1 rounded-tl-xl`}
                           onClick={() => setSeenSection(1)}
                        >
                           <span>
                              <img
                                 src={seenSection === 1 ? IvoryList : IvoryGold}
                                 alt="list-item"
                                 className="w-3 mt-1"
                              />
                           </span>
                           <span>{t('seminars.content')}</span>
                        </button>
                        :
                        null
               }
               {token && (
                  <>
                     {
                        images?.length > 0 &&
                        <button
                           className={`border px-3 py-1 ${seenSection === 2 ? "seenSection" : "unSeenSection"
                              } flex items-center gap-2 rounded-md`}
                           onClick={() => setSeenSection(2)}
                        >
                           <span>
                              <img
                                 src={seenSection === 2 ? IvoryList : IvoryGold}
                                 alt="list-item"
                                 className="w-3 mt-1"
                              />
                           </span>
                           <span>{t('seminars.imgs')}</span>
                        </button>
                     }
                     <button
                        className={`border px-3 py-1 ${seenSection === 3 ? "seenSection" : "unSeenSection"
                           } flex items-center gap-2 rounded-md`}
                        onClick={() => setSeenSection(3)}
                     >
                        <span>
                           <img
                              src={seenSection === 3 ? IvoryList : IvoryGold}
                              alt="list-item"
                              className="w-3 mt-1"
                           />
                        </span>
                        <span>{t('seminars.vids')}</span>
                     </button>
                  </>
               )}
               {
                  dateState.state &&
                  <button
                     className={`border px-3 py-1 ${seenSection === 4 ? "seenSection" : "unSeenSection"
                        } flex items-center gap-2 rounded-md`}
                     onClick={async () => {
                        if (dateState.state) {
                           await registerForSeminar();
                        }
                        setSeenSection(4);
                     }}
                  >
                     <span>
                        <img
                           src={seenSection === 4 ? IvoryList : IvoryGold}
                           alt="list-item"
                           className="w-3 mt-1"
                        />
                     </span>
                     <span>{t('custom.check')}</span>
                  </button>
               }
               {
                  details?.courses[0]?.trainingProgram?.length > 0 &&
                  <button
                     className={`border ${seenSection === 5 ? "seenSection" : "unSeenSection"} flex items-center gap-2 px-3 py-1 rounded-md`}
                     onClick={() => setSeenSection(5)}
                  >
                     <span>
                        <img
                           src={seenSection === 5 ? IvoryList : IvoryGold}
                           alt="list-item"
                           className="w-3 mt-1"
                        />
                     </span>
                     <span> {t('seminars.targets')}</span>
                  </button>
               }
            </div>
            <div className="mt-4 max-lg:mt-10">{returnedSection[seenSection]}</div>
         </div>
      </div>
   );
};
export default SeminarDetailBox;